import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BackOfficeSidebar } from '../navigation/BackOfficeSideNav.jsx';
import { TopBar } from '../navigation/TopNav.jsx';
import api, { handleApiError } from '../../api.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { connectSocket, disconnectSocket } from '../../lib/socket.js';

/**
 * BackOfficeLayout - Root layout for all authenticated back-office pages.
 * Uses React Router <Outlet /> for nested route rendering.
 */
export default function BackOfficeLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [staff, setStaff] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [fiscalStatus, setFiscalStatus] = useState(null);
  const [timeDriftMinutes, setTimeDriftMinutes] = useState(null);

  // Derive active section from URL path
  const activeSection = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/analytics')) return 'analytics';
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/menu')) return 'menu';
    if (path.startsWith('/inventory')) return 'inventory';
    if (path.startsWith('/suppliers')) return 'suppliers';
    if (path.startsWith('/deliveries')) return 'deliveries';
    if (path.startsWith('/staff')) return 'staff';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/discounts')) return 'discounts';
    if (path.startsWith('/reports')) return 'reports';
    return 'dashboard';
  }, [location.pathname]);

  // Load staff info from AuthContext or localStorage
  useEffect(() => {
    if (auth.user && auth.venue) {
      const userRoles = auth.user.roles || auth.roles || [];
      const roleCodes = userRoles.map(r => {
        if (typeof r === 'string') return r.toUpperCase();
        return (r?.code || '').toUpperCase();
      });

      setStaff({
        ...auth.user,
        venue_id: auth.venue.id,
        venueId: auth.venue.id,
        id: auth.user.id,
        staffId: auth.user.id,
        role: roleCodes[0]?.toLowerCase() || 'staff',
        roles: roleCodes,
        name: auth.user.first_name && auth.user.last_name
          ? `${auth.user.first_name} ${auth.user.last_name}`
          : auth.user.operator_code || 'User',
      });
      return;
    }

    const staffStr = localStorage.getItem("staff");
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");

    if (!staffStr || !token) {
      navigate("/login");
    } else {
      try {
        setStaff(JSON.parse(staffStr));
      } catch (e) {
        console.error("Failed to parse staff data:", e);
        navigate("/login");
      }
    }
  }, [auth.user, auth.venue, auth.roles, navigate]);

  // Connect WebSocket when authenticated, disconnect on unmount / logout
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const venueId = staff?.venue_id || staff?.venueId;
    if (!token || !venueId) return;

    connectSocket(token, venueId, queryClient);

    return () => {
      disconnectSocket();
    };
  }, [staff?.venue_id, staff?.venueId, queryClient]);

  const handleNavigate = useCallback((path) => {
    if (!path) return;
    setIsSidebarOpen(false);
    navigate(path);
  }, [navigate]);

  // Check fiscal unit status
  useEffect(() => {
    const isAuthenticated = auth.status === 'ready' && (auth.user != null || !!localStorage.getItem('staff'));
    if (!isAuthenticated) {
      setFiscalStatus(null);
      return;
    }
    const fetchFiscalStatus = async () => {
      try {
        const data = await api.fiscal.status();
        setFiscalStatus(data);
      } catch (err) {
        console.error('Failed to load fiscal status:', err);
        setFiscalStatus(null);
      }
    };
    fetchFiscalStatus();
  }, [auth.status, auth.user]);

  // Check time sync
  useEffect(() => {
    const checkTimeSync = async () => {
      try {
        const clientBefore = Date.now();
        const data = await api.getServerTime();
        const clientAfter = Date.now();
        const clientMid = (clientBefore + clientAfter) / 2;
        const serverEpochMs =
          typeof data?.serverEpochMs === 'number'
            ? data.serverEpochMs
            : data?.nowUtc
            ? new Date(data.nowUtc).getTime()
            : null;
        if (!serverEpochMs) return;
        setTimeDriftMinutes(Math.round((clientMid - serverEpochMs) / 60000));
      } catch (err) {
        console.error('Failed to check time synchronization:', err);
      }
    };
    checkTimeSync();
  }, []);

  const handleLogout = useCallback(() => {
    disconnectSocket();
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("staff");
    navigate('/login');
  }, [navigate]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  return (
    <div className="flex h-screen bg-void overflow-hidden">
      <BackOfficeSidebar
        activeSection={activeSection}
        staff={staff}
        onNavigate={handleNavigate}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={closeSidebar}
          role="presentation"
        />
      )}

      <div className="relative z-20 flex-1 flex flex-col overflow-hidden">
        <TopBar
          onLogout={handleLogout}
          onToggleSidebar={handleToggleSidebar}
          isSidebarOpen={isSidebarOpen}
          showSearch={false}
        />

        {fiscalStatus && !fiscalStatus.ready && (
          <div className="px-4 py-2 bg-red-900/70 text-red-100 text-sm border-b border-red-700">
            <p className="font-medium">
              No active fiscal unit is configured for this venue. Sales and payments that require a fiscal receipt are blocked.
            </p>
          </div>
        )}

        {timeDriftMinutes !== null && Math.abs(timeDriftMinutes) >= 2 && (
          <div className="px-4 py-2 bg-amber-900/70 text-amber-100 text-sm border-b border-amber-700">
            <p className="font-medium">
              Device time differs from official Bulgaria time by approximately{' '}
              {Math.abs(timeDriftMinutes)} minute{Math.abs(timeDriftMinutes) === 1 ? '' : 's'}.
            </p>
            <p className="text-amber-100/80">
              Please correct the system clock on this device so it matches the current time in Bulgaria
              (Europe/Sofia) to comply with fiscal regulations.
            </p>
          </div>
        )}

        {/* Nested route content */}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
